/* eslint-env jest */
import offeredOptions, { priceOf, hasArtwork } from './offeredOptions';

// Shapes taken from real pricelists: senior 745 @ Enochs and elementary 658 @ Central.
const option = (attrs) => ({
  id: attrs.id,
  name: attrs.name ?? '',
  description: attrs.description ?? '',
  code: attrs.code ?? null,
  featured: attrs.featured ?? false,
  imageUrl: attrs.imageUrl ?? null,
  seniorBuyAllPromo: attrs.seniorBuyAllPromo ?? false,
  isBuyAll: attrs.isBuyAll ?? false,
  prices: attrs.price === undefined ? [] : [{ price: attrs.price }],
});

const names = (list) => list.map((o) => o.name);

describe('offeredOptions', () => {
  it('drops ADD-ONS (options with a code) — they are sold elsewhere in the flow', () => {
    // "Magnets" (elementary) and "Select Yearbook Pose" (senior) both carry code=1.
    const result = offeredOptions([
      option({ id: 1, name: 'Basic Package', price: 45 }),
      option({ id: 2, name: 'Magnets', code: 1, price: 15 }),
      option({ id: 3, name: 'Select Yearbook Pose', code: 1, price: 0 }),
    ]);
    expect(names(result)).toEqual(['Basic Package']);
  });

  it('drops options this school never priced — a card with no price is a dead end', () => {
    const result = offeredOptions([
      option({ id: 1, name: 'Priced', price: 30 }),
      option({ id: 2, name: 'Unpriced' }),
    ]);
    expect(names(result)).toEqual(['Priced']);
  });

  it('keeps a $0 package (free is a price; only "no price row" is not)', () => {
    const result = offeredOptions([option({ id: 1, name: 'Free Yearbook Photo', price: 0 })]);
    expect(names(result)).toEqual(['Free Yearbook Photo']);
  });

  it('drops options with nothing to render — they would be empty cards', () => {
    const result = offeredOptions([
      option({ id: 1, name: '', description: '', price: 20 }),
      option({ id: 2, name: '', description: 'Set of 2 magnets', price: 20 }),
      option({ id: 3, name: '', imageUrl: 'https://s3/opt.png', price: 20 }),
      option({ id: 4, name: '', imageUrl: 'https://s3/missing.png', price: 20 }),
    ]);
    expect(result.map((o) => o.id)).toEqual([2, 3]);
  });

  it('leads with featured options, then the most expensive', () => {
    const result = offeredOptions([
      option({ id: 1, name: 'Cheap', price: 10 }),
      option({ id: 2, name: 'Dear', price: 90 }),
      option({ id: 3, name: 'Featured cheap', price: 20, featured: true }),
      option({ id: 4, name: 'Featured dear', price: 60, featured: true }),
    ]);
    expect(names(result)).toEqual(['Featured dear', 'Featured cheap', 'Dear', 'Cheap']);
  });

  it('applies the caller exclusion (seniors drop the $0 yearbook Option)', () => {
    const isYearbook = (o) => o.name.toLowerCase().includes('yearbook');
    const result = offeredOptions([
      option({ id: 1, name: 'Scholar’s Choice', price: 75 }),
      option({ id: 2, name: 'Yearbook Photo', price: 0 }),
    ], isYearbook);
    expect(names(result)).toEqual(['Scholar’s Choice']);
  });

  it('drops the senior_buy_all_promo upsell but keeps the full-price All Digital', () => {
    // Real shapes from pricelist 745: "All Digital" (2671) is a legitimate grid
    // package that ALSO carries a buy-all banner (isBuyAll), while "Buy All Your
    // Photos" (2692) is the post-purchase upsell, flagged senior_buy_all_promo.
    // The banner must NOT be the discriminator.
    const result = offeredOptions([
      option({
        id: 2671, name: 'All Digital', price: 316.76, featured: true, isBuyAll: true,
      }),
      option({
        id: 2692, name: 'Buy All Your Photos', price: 99.95, isBuyAll: true, seniorBuyAllPromo: true,
      }),
    ]);
    expect(names(result)).toEqual(['All Digital']);
  });

  it('survives junk input rather than blanking the screen', () => {
    expect(offeredOptions(null)).toEqual([]);
    expect(offeredOptions([])).toEqual([]);
  });
});

describe('priceOf / hasArtwork', () => {
  it('reads the school-scoped price row', () => {
    expect(priceOf(option({ id: 1, price: 42 }))).toBe(42);
    expect(priceOf(option({ id: 1 }))).toBeNull();
  });

  it('treats Paperclip’s missing.png as no artwork', () => {
    expect(hasArtwork({ imageUrl: 'https://s3/a/missing.png' })).toBe(false);
    expect(hasArtwork({ imageUrl: 'https://s3/a/real.png' })).toBe(true);
    expect(hasArtwork({})).toBe(false);
  });
});
